import React, { useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./CTAMultiStep.css";

// ✅ Define OUTSIDE the CTA component, at the top of the file
const PhoneInputField = React.forwardRef((props, ref) => (
  <input
    {...props}
    ref={ref}
    style={{
      border: "none",
      outline: "none",
      boxShadow: "none",
      width: "100%",
    }}
  />
));

const CTA = () => {
  // Multi-step form states
  const [currentStep, setCurrentStep] = useState(1);
  const [multiStepData, setMultiStepData] = useState({
    name: "",
    email: "",
    company: "",
    helpCategory: "",
    revenue: "",
    phone: "", // Will store full phone number with country code
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState({ uae: false, contactUs: false });

  const totalSteps = 4;

  const handleMultiStepInputChange = (e) => {
    const { name, value } = e.target;
    setMultiStepData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setApiError("");
  };

  const handlePhoneChange = (value) => {
    setMultiStepData((prev) => ({
      ...prev,
      phone: value,
    }));

    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
    setApiError("");
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setMultiStepData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setApiError("");
  };

  // Clean phone number to get only digits
  const getCleanPhoneNumber = (phone) => {
    if (!phone) return "";
    // Remove all non-digit characters
    return phone.replace(/\D/g, '');
  };

  // Validate phone number as per Contact Us API (minimum 10 digits)
  const validatePhone = (phone) => {
    if (!phone) return "Phone number is required";
    
    // Get only digits
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Check if phone number has at least 10 digits
    if (digitsOnly.length < 10) {
      return "Phone number must be at least 10 digits";
    }
    
    return "";
  };

  // Validate email format and business email
  const validateEmail = (email) => {
    if (!email) return "Email is required";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }

    const personalEmailProviders = [
      "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com",
      "icloud.com", "live.com", "msn.com", "ymail.com", "rocketmail.com",
      "rediffmail.com", "mail.com", "protonmail.com", "zoho.com",
    ];

    const domain = email.split("@")[1].toLowerCase();

    if (personalEmailProviders.includes(domain)) {
      return "Please enter a business email address";
    }

    return "";
  };

  // Validate current step
  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!multiStepData.name.trim()) {
        newErrors.name = "Name is required";
      }

      const emailError = validateEmail(multiStepData.email);
      if (emailError) {
        newErrors.email = emailError;
      }

      // Phone validation - mandatory (minimum 10 digits as per Contact Us API)
      const phoneError = validatePhone(multiStepData.phone);
      if (phoneError) {
        newErrors.phone = phoneError;
      }

      if (!multiStepData.company.trim()) {
        newErrors.company = "Company is required";
      }
    } else if (currentStep === 2) {
      if (!multiStepData.helpCategory) {
        newErrors.helpCategory = "Please select a service";
      }
    } else if (currentStep === 3) {
      if (!multiStepData.revenue) {
        newErrors.revenue = "Please select your annual revenue";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation functions
  const nextStep = () => {
    if (validateStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      setApiError("");
    }
  };

  // Format service name for display
  const formatServiceName = (service) => {
    const serviceMap = {
      "VIRTUAL_CFO_SERVICES": "CFO Services",
      "INTERIM_CFO_SERVICES": "Interim CFO Services",
      "TAXATION_ADVISORY": "Taxation Advisory",
      "M_AND_A": "M&A",
      "ERP_IMPLEMENTATION": "ERP Implementation",
      "TECHNICAL_ACCOUNTING": "Technical Accounting",
      "IPO": "IPO",
      "CTO_SERVICES": "CTO Services",
      "CHRO_SERVICES": "CHRO Services",
      "COMPLIANCE": "Compliance"
    };
    return serviceMap[service] || service;
  };

  // Format revenue for display
  const formatRevenue = (revenue) => {
    const revenueMap = {
      "BELOW_TWENTY_FIVE_CR": "Below 25 Cr",
      "TWENTY_FIVE_CR_TO_FIFTY_CR": "25 Cr to 50 Cr",
      "FIFTY_CR_TO_HUNDRED_CR": "50 Cr to 100 Cr",
      "MORE_THAN_HUNDRED_CR": "More than 100 Cr"
    };
    return revenueMap[revenue] || revenue;
  };

  // Get description for current step (based on the PHP code)
  const getDescriptionForCurrentStep = () => {
    if (multiStepData.helpCategory) {
      const purpose = multiStepData.helpCategory;
      const formattedPurpose = purpose.split('_').map(s => s.toLowerCase()).join(' ');
      return `The client is exploring ${formattedPurpose}`;
    }
    return undefined;
  };

  // Make API call to UAE endpoint
  const callUaeApi = async (payload) => {
    try {
      console.log("Calling UAE API with payload:", payload);
      
      const response = await fetch("https://cfobridge.com/insertcontactuae", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload),
      });

      let result;
      try {
        result = await response.json();
      } catch (e) {
        result = { status: false, msg: "Invalid response format" };
      }

      console.log("UAE API Response:", { status: response.status, result });

      if (response.ok && result.status === true) {
        return { 
          success: true, 
          message: result.msg || "UAE API success",
          data: result 
        };
      } else {
        return { 
          success: false, 
          message: result.msg || `UAE API error (${response.status})`,
          status: response.status 
        };
      }
    } catch (error) {
      console.error("UAE API Network Error:", error);
      return { 
        success: false, 
        message: error.message || "Network error - UAE API",
        error: error 
      };
    }
  };

  // Make API call to Contact Us endpoint - EXACTLY as per PHP reference
  const callContactUsApi = async () => {
    try {
      // Clean phone number to get only digits (minimum 10 digits validation)
      let cleanPhoneNumber = multiStepData.phone ? getCleanPhoneNumber(multiStepData.phone) : undefined;
      
      // Prepare the API payload exactly as in the PHP code
      const apiPayload = {
        name: multiStepData.name || undefined,
        revenue: multiStepData.revenue || 'BELOW_TWENTY_FIVE_CR',
        email: multiStepData.email || undefined,
        company: multiStepData.company || undefined,
        phoneNumber: cleanPhoneNumber, // Send only numeric digits
        bdPartnerCfoId: undefined, // Not collected in this form
        description: getDescriptionForCurrentStep(),
        bookingTime: undefined, // Not collected in this form
        helpCategory: multiStepData.helpCategory
      };

      console.log("Calling Contact Us API with payload:", apiPayload);
      
      const response = await fetch("https://cfo-bridge-api.cfobridge.com/api/public/contactUs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(apiPayload)
      });

      let result;
      try {
        result = await response.json();
      } catch (e) {
        result = { success: false, message: "Invalid response format" };
      }

      console.log("Contact Us API Response:", { status: response.status, result });

      if (response.ok) {
        return { 
          success: true, 
          message: "Contact Us API success",
          data: result 
        };
      } else {
        console.error("Contact Us API Error Details:", {
          status: response.status,
          statusText: response.statusText,
          result: result
        });
        
        return { 
          success: false, 
          message: result.message || result.msg || `Contact Us API error (${response.status})`,
          status: response.status,
          details: result
        };
      }
    } catch (error) {
      console.error("Contact Us API Network Error:", error);
      return { 
        success: false, 
        message: error.message || "Network error - Contact Us API",
        error: error 
      };
    }
  };

  // Submit form - call both APIs independently
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep()) {
      return;
    }

    setIsSubmitting(true);
    setApiError("");
    setApiSuccess({ uae: false, contactUs: false });

    try {
      // Prepare data for UAE API
      const uaeApiPayload = {
        name: multiStepData.name,
        email: multiStepData.email,
        company: multiStepData.company,
        type: formatServiceName(multiStepData.helpCategory),
        phone: multiStepData.phone || "", // Full phone number with country code
        message: `Annual Revenue: ${formatRevenue(multiStepData.revenue)}. Service Interest: ${formatServiceName(multiStepData.helpCategory)}`,
        page_url: window.location.href,
        formt: "ctform",
        privacychecked: 1,
        whatsappchecked: 0,
        notjobchecked: 1,
      };

      console.log("=== SUBMITTING FORM DATA ===");
      console.log("Form Data:", multiStepData);
      console.log("UAE API Payload:", uaeApiPayload);

      // Call both APIs in parallel
      const [uaeResult, contactUsResult] = await Promise.allSettled([
        callUaeApi(uaeApiPayload),
        callContactUsApi() // Contact Us API doesn't need payload as it's built in the function
      ]);

      // Process UAE API result
      let uaeSuccess = false;
      let uaeMessage = "";
      
      if (uaeResult.status === 'fulfilled') {
        uaeSuccess = uaeResult.value.success;
        uaeMessage = uaeResult.value.message;
        if (uaeSuccess) {
          console.log("✅ UAE API SUCCESS - Data stored in database and email sent");
        } else {
          console.error("❌ UAE API FAILED:", uaeResult.value.message);
        }
      } else {
        uaeMessage = uaeResult.reason?.message || "UAE API failed";
        console.error("❌ UAE API REJECTED:", uaeResult.reason);
      }

      // Process Contact Us API result
      let contactUsSuccess = false;
      let contactUsMessage = "";
      let contactUsDetails = null;
      
      if (contactUsResult.status === 'fulfilled') {
        contactUsSuccess = contactUsResult.value.success;
        contactUsMessage = contactUsResult.value.message;
        contactUsDetails = contactUsResult.value.details;
        
        if (contactUsSuccess) {
          console.log("✅ CONTACT US API SUCCESS");
        } else {
          console.error("❌ CONTACT US API FAILED:", contactUsResult.value.message);
          if (contactUsDetails) {
            console.error("Contact Us API Error Details:", contactUsDetails);
          }
        }
      } else {
        contactUsMessage = contactUsResult.reason?.message || "Contact Us API failed";
        console.error("❌ CONTACT US API REJECTED:", contactUsResult.reason);
      }

      // Update success states
      setApiSuccess({ 
        uae: uaeSuccess, 
        contactUs: contactUsSuccess 
      });

      // Determine overall result
      if (uaeSuccess && contactUsSuccess) {
        // Both APIs succeeded
        console.log("✅✅ BOTH APIS SUCCESSFUL - Form fully processed");
        setCurrentStep(totalSteps);
      } else if (uaeSuccess || contactUsSuccess) {
        // At least one API succeeded
        console.log("⚠️ PARTIAL SUCCESS - One API succeeded");
        setCurrentStep(totalSteps);
        
        // Store which API failed in session for debugging
        sessionStorage.setItem('api_failures', JSON.stringify({
          uae: !uaeSuccess ? uaeMessage : null,
          contactUs: !contactUsSuccess ? contactUsMessage : null
        }));
      } else {
        // Both APIs failed
        console.error("❌❌ BOTH APIS FAILED");
        const errorMessages = [];
        if (uaeMessage) errorMessages.push(`UAE: ${uaeMessage}`);
        if (contactUsMessage) errorMessages.push(`Contact: ${contactUsMessage}`);
        
        setApiError(
          "Unable to submit form. " + errorMessages.join(". ")
        );
      }
    } catch (error) {
      console.error("UNEXPECTED ERROR:", error);
      setApiError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate progress percentage
  const progressPercentage = (currentStep / (totalSteps - 1)) * 100;

  // Render form fields based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="contact-us-form-step active">
            <div className="sec-head">
              <h2>Contact Our Team</h2>
            </div>
            <p className="para">
              Gain access to high-level financial expertise without the overhead of a full-time hire.
            </p>
            <div className="contact-us-form-step-content">
              <div className="contact-us-form-group">
                <label htmlFor="name" className="contact-us-form-label">
                  Your Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  id="name"
                  name="name"
                  className={`contact-us-form-input ${errors.name ? "error" : ""}`}
                  value={multiStepData.name}
                  onChange={handleMultiStepInputChange}
                />
                {errors.name && (
                  <div className="contact-us-error-message">{errors.name}</div>
                )}
              </div>
              
              <div className="contact-us-form-group">
                <label htmlFor="email" className="contact-us-form-label">
                  Business Email *
                </label>
                <input
                  type="email"
                  placeholder="name@company.com"
                  id="email"
                  name="email"
                  className={`contact-us-form-input ${errors.email ? "error" : ""}`}
                  value={multiStepData.email}
                  onChange={handleMultiStepInputChange}
                />
                {errors.email && (
                  <div className="contact-us-error-message">{errors.email}</div>
                )}
              </div>
              
              <div className="contact-us-form-group">
                <label htmlFor="phone" className="contact-us-form-label">
                  Phone Number *
                </label>
                <PhoneInput
  international
  defaultCountry="AE"
  value={multiStepData.phone}
  onChange={handlePhoneChange}
  className={`contact-us-form-input ${errors.phone ? "error" : ""}`}
  placeholder="Enter phone number"
  inputComponent={PhoneInputField}    // 👈 stable reference, no remount
/>
                {errors.phone && (
                  <div className="contact-us-error-message">{errors.phone}</div>
                )}
              </div>
              
              <div className="contact-us-form-group">
                <label htmlFor="company" className="contact-us-form-label">
                  Company Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter your company name"
                  id="company"
                  name="company"
                  className={`contact-us-form-input ${errors.company ? "error" : ""}`}
                  value={multiStepData.company}
                  onChange={handleMultiStepInputChange}
                />
                {errors.company && (
                  <div className="contact-us-error-message">{errors.company}</div>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="contact-us-form-step active">
            <div className="sec-head">
              <h2>Contact Our Team</h2>
            </div>
            <p className="para">
              What service are you interested in? *
            </p>
            <div className="contact-us-form-step-content">
              <div className="contact-us-radio-group">
                {[
                  { value: "VIRTUAL_CFO_SERVICES", label: "CFO Services" },
                  { value: "INTERIM_CFO_SERVICES", label: "E-Invoicing Setup & Compliance" },
                  { value: "TAXATION_ADVISORY", label: "Company Setup & Winding Down" },
                  { value: "M_AND_A", label: "M&A" },
                  { value: "ERP_IMPLEMENTATION", label: "ERP Implementation" },
                  { value: "TECHNICAL_ACCOUNTING", label: "Working Capital Management" },
                  { value: "IPO", label: "IPO Readiness" },
                  { value: "CTO_SERVICES", label: "CTO Services" },
                  { value: "CHRO_SERVICES", label: "CHRO Services" },
                  { value: "COMPLIANCE", label: "AML & VAT" },
                ].map((option) => (
                  <label key={option.value} className="contact-us-radio-option">
                    <input
                      type="radio"
                      name="helpCategory"
                      value={option.value}
                      checked={multiStepData.helpCategory === option.value}
                      onChange={handleRadioChange}
                    />
                    <span className="contact-us-radio-custom"></span>
                    <span className="contact-us-radio-label">{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.helpCategory && (
                <div className="contact-us-error-message">{errors.helpCategory}</div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="contact-us-form-step active">
            <div className="sec-head">
              <h2>Contact Our Team</h2>
            </div>
            <p className="para">
              What's your annual revenue? *
            </p>
            <div className="contact-us-form-step-content">
              <div className="contact-us-radio-group">
                {[
                  { value: "BELOW_TWENTY_FIVE_CR", label: "Below 25 Cr" },
                  { value: "TWENTY_FIVE_CR_TO_FIFTY_CR", label: "25 Cr to 50 Cr" },
                  { value: "FIFTY_CR_TO_HUNDRED_CR", label: "50 Cr to 100 Cr" },
                  { value: "MORE_THAN_HUNDRED_CR", label: "More than 100 Cr" },
                ].map((option) => (
                  <label key={option.value} className="contact-us-radio-option">
                    <input
                      type="radio"
                      name="revenue"
                      value={option.value}
                      checked={multiStepData.revenue === option.value}
                      onChange={handleRadioChange}
                    />
                    <span className="contact-us-radio-custom"></span>
                    <span className="contact-us-radio-label">{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.revenue && (
                <div className="contact-us-error-message">{errors.revenue}</div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="contact-us-form-step active">
            <div className="contact-us-success-content">
              <div className="contact-us-checkmark">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#014293"
                  strokeWidth="2"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h2 className="contact-us-success-heading">
                Thank You!
              </h2>
              <p className="contact-us-success-subheading">
                We've received your information. Our team will reach out to you shortly.
              </p>
              {(!apiSuccess.uae || !apiSuccess.contactUs) && (
                <p className="contact-us-success-subheading" style={{ fontSize: "14px", marginTop: "10px", color: "#666" }}>
                  {!apiSuccess.uae && !apiSuccess.contactUs ? 
                    "Note: Some services are temporarily unavailable, but we'll still contact you." :
                    "Note: One of our services is temporarily unavailable, but your request was received."}
                </p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
  <section className="cta-section-modern" id="contact">
    <div className="cta-container-modern">
      <div className="cta-header-modern">
        <h2 className="cta-title-modern">Get in Touch</h2>
        <p className="cta-subtitle-modern">
          Ready to elevate your business? Contact us today for expert financial guidance.
        </p>
      </div>

      <div className="cta-content-modern">
        {/* Contact Information */}
        <div className="contact-cards-modern">
          <div className="contact-card-modern">
            <div className="contact-icon-modern">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
            </div>
            <h3 className="contact-title-modern">Phone Number</h3>
            <div className="contact-details-modern">
              <p>+971 50 167 4938</p>
            </div>
          </div>

          <div className="contact-card-modern">
            <div className="contact-icon-modern">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </div>
            <h3 className="contact-title-modern">Email Address</h3>
            <div className="contact-details-modern">
              <p>vidya@cfobridge.com</p>
            </div>
          </div>

          <div className="contact-card-modern">
  <div className="contact-icon-modern">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  </div>
  <h3 className="contact-title-modern">Response Time</h3>
  <div className="contact-details-modern">
    <p>We respond to all enquiries within 24 hours</p>
  </div>
</div>

<div className="contact-card-modern">
  <div className="contact-icon-modern">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  </div>
  <h3 className="contact-title-modern">Free Consultation</h3>
  <div className="contact-details-modern">
    <p>Book a free 30-min strategy call with senior CFO advisors</p>
  </div>
</div>
        </div>

          {/* Multi-step Form */}
          <div className="col-lg-5 offset-lg-1 col-12" style={{ padding: 0 }}>
            <div className="contact-us-multi-step-section">
              <div className="contact-us-multi-step-container">
                <div className="contact-us-multi-step-content">
                  <div className="contact-us-multi-step-right">
                    <div className="contact-us-multi-step-form-container">
                      <form onSubmit={handleSubmit}>
                        <div className="contact-us-multi-step-form-steps">
                          {renderStepContent()}
                        </div>

                        {/* Error Message */}
                        {apiError && currentStep < 4 && (
                          <div className="contact-us-error-message" style={{ marginBottom: "15px", textAlign: "center" }}>
                            {apiError}
                          </div>
                        )}

                        {currentStep < 4 && (
                          <>
                            <div className="contact-us-form-navigation">
                              <button
                                type="button"
                                className={`contact-us-btn contact-us-btn-secondary contact-us-btn-prev ${currentStep === 1 ? "disabled" : ""}`}
                                onClick={previousStep}
                                disabled={currentStep === 1}
                              >
                                Previous
                              </button>

                              {currentStep < 3 && (
                                <button
                                  type="button"
                                  className="contact-us-btn contact-us-btn-primary contact-us-btn-next"
                                  onClick={nextStep}
                                >
                                  Next
                                </button>
                              )}

                              {currentStep === 3 && (
                                <button
                                  type="submit"
                                  className="contact-us-btn contact-us-btn-primary contact-us-btn-submit"
                                  disabled={isSubmitting}
                                >
                                  {isSubmitting ? "Submitting..." : "Submit"}
                                </button>
                              )}
                            </div>

                            <div className="contact-us-multi-step-progress">
                              <div className="contact-us-progress-bar">
                                <div
                                  className="contact-us-progress-fill"
                                  style={{ width: `${progressPercentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
         {/* Full-width map — OUTSIDE both containers, edge to edge */}
    <div className="contact-map-fullwidth">
      <iframe
        src="https://www.google.com/maps/d/embed?mid=1SWd6vfjzeRrH2EZ08M0zZjiOG2OlZXU&ehbc=2E312F"
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title="CFO Bridge UAE Location"
      />
    </div>
      </div>
    </section>
  );
};

export default CTA;